import type { Lang } from "../types.ts";
import { t } from "../i18n/ui.ts";

export interface ToolbarAction {
  label: string;
  onClick: () => void;
  pressed?: boolean;
}

export interface PositionDot {
  index: number;
  label: string;
  answered: boolean;
  current: boolean;
  marked: boolean;
  video: boolean;
}

interface Props {
  lang: Lang;
  /** Left-hand tabs: topic block and licence class, as on the official screen. */
  basicLabel: string;
  classLabel: string;
  /** Exam mode: number of questions still open. */
  remaining?: number;
  /** Free-form status text used by practice mode instead of `remaining`. */
  status?: string;
  marked: boolean;
  onToggleMark: () => void;
  /** Red button. Omitted in practice mode. */
  onEvaluate?: () => void;
  /** Extra neutral buttons, e.g. "show translation" / "show explanation". */
  extraActions?: ToolbarAction[];
  /** Green button on the right. */
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  onPrev: () => void;
  prevDisabled?: boolean;
  positions?: PositionDot[];
  onJump?: (index: number) => void;
  /**
   * First / back / forward / last strip, as on the official mobile screen.
   * Used where the pool is too long for the numbered grid.
   */
  paginator?: { index: number; total: number; onJump: (index: number) => void };
}

/**
 * The green control strip beneath the question: topic tabs, status, the
 * evaluate/mark/next buttons and the numbered question jump grid.
 */
export function ExamToolbar({
  lang, basicLabel, classLabel, remaining, status, marked, onToggleMark,
  onEvaluate, extraActions, onNext, nextLabel, nextDisabled,
  onPrev, prevDisabled, positions, onJump, paginator,
}: Props) {
  return (
    <div className="tbar">
      <div className="tbar__row">
        <div className="tbar__tabs">
          {basicLabel && <span className="tbar__tab tbar__tab--basic">{basicLabel}</span>}
          <span className="tbar__tab tbar__tab--class" translate="no">{classLabel}</span>
        </div>

        <p className="tbar__status" aria-live="polite">
          {status ??
            (remaining !== undefined ? (
              <>
                <span aria-hidden="true">⚠ </span>
                {t(lang, "remainingTasks", { n: remaining })}
              </>
            ) : null)}
        </p>

        <div className="tbar__actions">
          <button
            type="button"
            className="tbtn tbtn--plain"
            onClick={onPrev}
            disabled={prevDisabled}
          >
            ← {t(lang, "previous")}
          </button>

          {onEvaluate && (
            <button type="button" className="tbtn tbtn--red" onClick={onEvaluate}>
              {t(lang, "evaluate")}
            </button>
          )}

          {extraActions?.map((action) => (
            <button
              key={action.label}
              type="button"
              className="tbtn tbtn--plain"
              onClick={action.onClick}
              aria-pressed={action.pressed}
            >
              {action.label}
            </button>
          ))}

          <button
            type="button"
            className={`tbtn tbtn--yellow ${marked ? "is-marked" : ""}`}
            onClick={onToggleMark}
            aria-pressed={marked}
            title={t(lang, marked ? "unmarkQuestion" : "markQuestion")}
            aria-label={t(lang, marked ? "unmarkQuestion" : "markQuestion")}
          >
            {marked ? "★" : "☆"}
          </button>

          <button
            type="button"
            className="tbtn tbtn--green"
            onClick={onNext}
            disabled={nextDisabled}
          >
            {nextLabel ?? t(lang, "next")}
          </button>
        </div>
      </div>

      {paginator && paginator.total > 1 && (
        <nav className="tnav" aria-label={t(lang, "jumpToQuestion")}>
          <PageStep
            label={t(lang, "first")}
            glyph="|◀"
            to={0}
            {...paginator}
          />
          <PageStep
            label={t(lang, "back10")}
            glyph="◀◀"
            to={paginator.index - 10}
            {...paginator}
          />
          <PageStep
            label={t(lang, "previous")}
            glyph="◀"
            to={paginator.index - 1}
            {...paginator}
          />
          <span className="tnav__pos" aria-live="polite">
            {paginator.index + 1} / {paginator.total}
          </span>
          <PageStep
            label={t(lang, "next")}
            glyph="▶"
            to={paginator.index + 1}
            {...paginator}
          />
          <PageStep
            label={t(lang, "forward10")}
            glyph="▶▶"
            to={paginator.index + 10}
            {...paginator}
          />
          <PageStep
            label={t(lang, "last")}
            glyph="▶|"
            to={paginator.total - 1}
            {...paginator}
          />
        </nav>
      )}

      {positions && onJump && (
        <nav className="tpos" aria-label={t(lang, "jumpToQuestion")}>
          {positions.map((p) => (
            <button
              key={p.index}
              type="button"
              className={[
                "tpos__btn",
                p.answered && "tpos__btn--done",
                p.current && "tpos__btn--current",
              ].filter(Boolean).join(" ")}
              aria-current={p.current ? "true" : undefined}
              aria-label={`${t(lang, "question")} ${p.label} — ${
                t(lang, p.answered ? "answered" : "unanswered")
              }${p.marked ? `, ${t(lang, "markQuestion")}` : ""}`}
              onClick={() => onJump(p.index)}
            >
              {p.video && <span className="tpos__flag" aria-hidden="true">▶</span>}
              {p.marked && <span className="tpos__star" aria-hidden="true">★</span>}
              {p.label}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

/** One step button in the paginator; disabled when it would not move. */
function PageStep({
  label, glyph, to, index, total, onJump,
}: {
  label: string;
  glyph: string;
  to: number;
  index: number;
  total: number;
  onJump: (index: number) => void;
}) {
  const target = Math.max(0, Math.min(total - 1, to));
  return (
    <button
      type="button"
      className="tnav__btn"
      onClick={() => onJump(target)}
      disabled={target === index}
      aria-label={label}
      title={label}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}
